import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useEffect, useMemo } from 'react';

import mixpanelEvents from '@/shared/lib/analytics/mixpanel-events';

const loadMixpanel = () => import('mixpanel-browser').then((m) => m.default);

let mixpanelPromise: ReturnType<typeof loadMixpanel> | null = null;

const getMixpanel = () => {
  if (!mixpanelPromise) mixpanelPromise = loadMixpanel();
  return mixpanelPromise;
};

const useMixpanel = (): {
  trackEvent: (event: string, properties?: Record<string, any>) => void;
  logout: () => void;
} => {
  const { keycloak } = useKeycloak();

  const userType = useMemo(() => {
    const roles = keycloak.realmAccess?.roles ?? [];

    if (roles.includes('backoffice') && window.location.hostname.includes('backoffice'))
      return 'backoffice';
    else if (roles.includes('location') && window.location.hostname.includes('location'))
      return 'location';
    else if (roles.includes('advertiser') && window.location.hostname.includes('advertiser'))
      return 'advertiser';
    else return 'service';
  }, [keycloak.realmAccess?.roles]);

  const trackEvent = useCallback((event: string, properties?: Record<string, any>) => {
    getMixpanel()
      .then((mp) => mp.track(event, properties ?? {}))
      .catch((e) => console.log('mixpanel error', e));
  }, []);

  const logout = useCallback(() => {
    getMixpanel()
      .then((mp) => {
        mp.track(mixpanelEvents.logOut);
        mp.stop_session_recording();
      })
      .catch((e) => console.log('mixpanel error', e));
  }, []);

  useEffect(() => {
    if (!keycloak.authenticated) return;

    keycloak.loadUserInfo().then((userInfo: any) => {
      getMixpanel().then((mp) => {
        mp.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
          debug: false,
          track_pageview: false,
          persistence: 'localStorage',
          record_sessions_percent: import.meta.env.MODE !== 'localhost' ? 100 : 0,
          record_mask_text_selector: '',
        });

        mp.identify(userInfo.sub);

        mp.people.set({
          $name: userInfo.name,
          $email: userInfo.email,
          'User type': userType,
          Module: userType,
        });

        if (import.meta.env.MODE !== 'localhost') {
          mp.start_session_recording();
        }
      });
    });
  }, [keycloak, userType]);

  return { trackEvent, logout };
};

export default useMixpanel;
